import { ZodError } from 'zod';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  HydratedStory,
  PointApiResponse,
  ReducedTrail,
  isAnswerGatePage,
  messageDeliveryFor,
} from 'magical-trails';

import { StorySelectorState, TrailReducer } from './types';
import hydrateStory from '../../utils/hydrateStory';
import {
  getCodesInLocalStorage,
  getSavedStoriesProgress,
  saveStory,
  saveTrail,
} from '../../utils/localStorage';
import setStoryPointsStatus from '../../utils/set-story-points-status';
import trailProgress from '../../utils/trailProgress';

const initialState: TrailReducer = {
  trailLoaded: false,
  loading: false,
  codesInLocalStorage: getCodesInLocalStorage(),
  savedStoriesProgress: getSavedStoriesProgress(),
  scannedPointQueue: [],
};

/**
 * `scannedPointQueue` opens points full screen, one after another. In a
 * phone-shell story a point that arrives as a message must never go through
 * it, or the same content would land twice: once in its thread and once
 * full screen. Everything else (and every story outside the phone shell,
 * where `messageDeliveryFor` is always undefined) queues as before.
 */
function fullScreenOnly(story: HydratedStory, codes: string[]): string[] {
  return codes.filter((code) => {
    const point = story.storyPoints.find((p) => p.code === code);
    return !point || !messageDeliveryFor(point, story.config);
  });
}

/** Unviewed introductions open as soon as the story loads. */
function introductionQueue(story: HydratedStory): string[] {
  return fullScreenOnly(
    story,
    story.storyPoints
      .filter(
        (storyPoint) => storyPoint.type === 'introduction' && !storyPoint.viewed
      )
      .map((storyPoint) => storyPoint.code)
  );
}

const trail = createSlice({
  name: 'trail',
  initialState,
  reducers: {
    setLoading: (state) => {
      state.loading = true;
      // A fresh code attempt clears any prior invite-only gate and error.
      state.inviteRequired = undefined;
      state.apiError = undefined;
    },
    setInviteRequired: (state, action: PayloadAction<{ code: string }>) => {
      state.loading = false;
      state.trailLoaded = false;
      state.inviteRequired = action.payload;
    },
    clearInviteRequired: (state) => {
      state.inviteRequired = undefined;
    },
    loadTrail: (
      state,
      action: PayloadAction<{ response: PointApiResponse; story: string }>
    ) => {
      state.trailLoaded = true;
      state.loading = false;
      state.response = action.payload.response;
      state.currentTrail = action.payload.response.trail;
      state.storySelector = undefined;
      state.inviteRequired = undefined;
      saveTrail(action.payload.response.trail);
      const currentStory = hydrateStory(
        action.payload.response.trail,
        action.payload.story
      );
      if (currentStory !== undefined) {
        state.currentStory = currentStory;
        saveStory(currentStory);
        const codesFromLoadedTrail: { [key: string]: string } = {};
        action.payload.response.trail.points.forEach((point) => {
          codesFromLoadedTrail[point.code] = action.payload.response.trail.id;
        });
        action.payload.response.trail.stories.forEach((story) => {
          codesFromLoadedTrail[story.code] = action.payload.response.trail.id;
        });
        state.codesInLocalStorage = {
          ...state.codesInLocalStorage,
          ...codesFromLoadedTrail,
        };
        state.scannedPointQueue = introductionQueue(currentStory);
        trailProgress({
          action: 'loadTrail',
          trailSessionToken: currentStory.trailSessionToken,
          trail: action.payload.response.trail.id,
          story: action.payload.story,
        });
      }
    },
    loadSavedTrail: (
      state,
      action: PayloadAction<{
        trail: ReducedTrail;
        story: string;
        // Set when restoreSavedTrail will immediately follow (resuming saved
        // progress). loadSavedTrail hydrates a FRESH story with a NEW session
        // token, but restoreSavedTrail then replaces currentStory with the
        // persisted one (its own token) and fires its own event — so emitting
        // here too would double-fire the load under two different tokens.
        suppressAnalytics?: boolean;
      }>
    ) => {
      state.trailLoaded = true;
      state.loading = false;
      state.currentTrail = action.payload.trail;
      state.storySelector = undefined;
      state.inviteRequired = undefined;
      const currentStory = hydrateStory(
        action.payload.trail,
        action.payload.story
      );
      if (currentStory !== undefined) {
        state.currentStory = currentStory;
        state.scannedPointQueue = introductionQueue(currentStory);
        if (!action.payload.suppressAnalytics) {
          trailProgress({
            action: 'loadSavedTrail',
            trailSessionToken: currentStory.trailSessionToken,
            trail: action.payload.trail.id,
            story: action.payload.story,
          });
        }
      }
    },
    restoreSavedTrail: (state, action: PayloadAction<HydratedStory>) => {
      state.trailLoaded = true;
      state.loading = false;
      state.storySelector = undefined;
      state.currentStory = action.payload;
      trailProgress({
        action: 'restoreSavedTrail',
        trailSessionToken: action.payload.trailSessionToken,
        trail: state.codesInLocalStorage[action.payload.code] || 'not-found',
        story: action.payload.code,
      });
    },
    // Hot-swap the loaded trail/story with freshly-fetched (republished)
    // content while the player is on it, WITHOUT minting a new session token or
    // firing analytics. The caller (utils/liveTrailUpdates) has already merged
    // the player's per-point progress into the fresh story.
    revalidateCurrentTrail: (
      state,
      action: PayloadAction<{ trail: ReducedTrail; story: HydratedStory }>
    ) => {
      state.currentTrail = action.payload.trail;
      if (state.response) {
        state.response = {
          ...state.response,
          trail: action.payload.trail,
        };
      }
      state.currentStory = action.payload.story;
      const refreshedCodes: { [key: string]: string } = {};
      action.payload.trail.points.forEach((point) => {
        refreshedCodes[point.code] = action.payload.trail.id;
      });
      action.payload.trail.stories.forEach((story) => {
        refreshedCodes[story.code] = action.payload.trail.id;
      });
      state.codesInLocalStorage = {
        ...state.codesInLocalStorage,
        ...refreshedCodes,
      };
      saveStory(action.payload.story);
    },
    setPointScanned: (state, action: PayloadAction<string>) => {
      if (state.currentStory?.storyPoints) {
        const { updatedStoryPoints, unlockedPointsQueue } =
          setStoryPointsStatus(
            state.currentStory?.storyPoints.map((point) => {
              if (point.code === action.payload && point.type === 'location') {
                point.scanned = new Date().toISOString();
              }
              return point;
            }),
            state.currentStory.config
          );
        state.currentStory.storyPoints = updatedStoryPoints;
        state.scannedPointQueue = [
          ...state.scannedPointQueue,
          ...fullScreenOnly(state.currentStory, unlockedPointsQueue),
        ];
        saveStory(state.currentStory);
      }
    },
    setCurrentPage: (
      state,
      action: PayloadAction<{ code: string; currentPage: number }>
    ) => {
      if (state.currentStory?.storyPoints) {
        const { updatedStoryPoints, unlockedPointsQueue } =
          setStoryPointsStatus(
            state.currentStory?.storyPoints.map((point) => {
              if (point.code === action.payload.code) {
                point.currentPage = action.payload.currentPage;
              }
              return point;
            }),
            state.currentStory.config
          );
        if (
          state.currentStory.storyPoints.find(
            (storyPoint) => storyPoint.code === action.payload.code
          )?.status !== 'completed' &&
          updatedStoryPoints.find(
            (storyPoint) => storyPoint.code === action.payload.code
          )?.status === 'completed'
        ) {
          trailProgress({
            action: 'storyPointCompleted',
            storyPoint: action.payload.code,
            trail: state.codesInLocalStorage[action.payload.code] || 'not-set',
            story: state.currentStory.code,
            trailSessionToken: state.currentStory.trailSessionToken,
          });
        }
        state.currentStory.storyPoints = updatedStoryPoints;
        state.scannedPointQueue = [
          ...state.scannedPointQueue,
          ...fullScreenOnly(state.currentStory, unlockedPointsQueue),
        ];
        saveStory(state.currentStory);
      }
    },
    setViewed: (state, action: PayloadAction<{ code: string }>) => {
      if (state.currentStory?.storyPoints) {
        const { updatedStoryPoints, unlockedPointsQueue } =
          setStoryPointsStatus(
            state.currentStory?.storyPoints.map((point) => {
              if (point.code === action.payload.code) {
                point.viewed = true;
              }
              return point;
            }),
            state.currentStory.config
          );
        state.currentStory.storyPoints = updatedStoryPoints;
        state.scannedPointQueue = [
          ...state.scannedPointQueue,
          ...fullScreenOnly(state.currentStory, unlockedPointsQueue),
        ];
        saveStory(state.currentStory);
      }
    },
    answerQuestion: (
      state,
      action: PayloadAction<{ code: string; page: number; userAnswer: string }>
    ) => {
      if (state.currentStory?.storyPoints) {
        state.currentStory.storyPoints = state.currentStory?.storyPoints.map(
          (point) => {
            if (point.code === action.payload.code) {
              // Question and Number Lock pages both carry a client-only
              // `userAnswer` that gates point completion.
              const target = point.pages?.[action.payload.page];
              if (isAnswerGatePage(target)) {
                target.userAnswer = action.payload.userAnswer;
              }
            }
            return point;
          }
        );
        saveStory(state.currentStory);
      }
    },
    // Phone shell: stamp when each newly unlocked message lands. Stamps are
    // written once and never moved, so running this on every change (as
    // usePhoneMessages does) can't deliver anything twice.
    stampDeliveries: (
      state,
      action: PayloadAction<{ code: string; deliveredAt: string }[]>
    ) => {
      if (!state.currentStory) return;
      let changed = false;
      for (const { code, deliveredAt } of action.payload) {
        const point = state.currentStory.storyPoints.find(
          (storyPoint) => storyPoint.code === code
        );
        if (!point || point.deliveredAt) continue;
        point.deliveredAt = deliveredAt;
        // A point that was an ordinary narrative when the player opened it,
        // and became a message in a live edit, arrives already "viewed".
        // The thread only sets that when the LAST page is done (see
        // advanceMessagePoint), so left alone it would complete the moment
        // the cursor reached the last page rather than when that page was.
        const delivery = messageDeliveryFor(point, state.currentStory.config);
        if (
          delivery?.presentation !== 'attachment' &&
          point.status !== 'completed'
        ) {
          point.viewed = false;
          point.currentPage = 0;
        }
        changed = true;
      }
      if (changed) saveStory(state.currentStory);
    },
    // Phone shell: the player has had these messages' thread open.
    markMessagesRead: (
      state,
      action: PayloadAction<{ codes: string[]; readAt: string }>
    ) => {
      if (!state.currentStory) return;
      let changed = false;
      for (const point of state.currentStory.storyPoints) {
        if (!action.payload.codes.includes(point.code) || point.readAt)
          continue;
        point.readAt = action.payload.readAt;
        changed = true;
      }
      if (changed) saveStory(state.currentStory);
    },
    // Phone shell: the conversation in one message point moves on a page.
    // `currentPage` is the thread's cursor; the point is only marked viewed
    // when its LAST page is done, which is what lets the ordinary status
    // pass call it completed (and unlock whatever waits on it).
    advanceMessagePoint: (state, action: PayloadAction<{ code: string }>) => {
      if (!state.currentStory?.storyPoints) return;
      const before = state.currentStory.storyPoints.find(
        (storyPoint) => storyPoint.code === action.payload.code
      );
      if (!before || before.status === 'completed') return;
      const lastPage = Math.max((before.pages?.length ?? 0) - 1, 0);
      const { updatedStoryPoints, unlockedPointsQueue } = setStoryPointsStatus(
        state.currentStory.storyPoints.map((point) => {
          if (point.code === action.payload.code) {
            const cursor = point.currentPage ?? 0;
            if (cursor < lastPage) {
              point.currentPage = cursor + 1;
            } else {
              point.currentPage = lastPage;
              point.viewed = true;
            }
          }
          return point;
        }),
        state.currentStory.config
      );
      if (
        updatedStoryPoints.find(
          (storyPoint) => storyPoint.code === action.payload.code
        )?.status === 'completed'
      ) {
        trailProgress({
          action: 'storyPointCompleted',
          storyPoint: action.payload.code,
          trail: state.codesInLocalStorage[action.payload.code] || 'not-set',
          story: state.currentStory.code,
          trailSessionToken: state.currentStory.trailSessionToken,
        });
      }
      state.currentStory.storyPoints = updatedStoryPoints;
      state.scannedPointQueue = [
        ...state.scannedPointQueue,
        ...fullScreenOnly(state.currentStory, unlockedPointsQueue),
      ];
      saveStory(state.currentStory);
    },
    addPointToScannedPointQueue: (state, action: PayloadAction<string>) => {
      state.scannedPointQueue = [...state.scannedPointQueue, action.payload];
    },
    openStorySelector: (state, action: PayloadAction<StorySelectorState>) => {
      state.loading = false;
      state.storySelector = action.payload;
    },
    closeStorySelector: (state) => {
      state.storySelector = undefined;
    },
    removePointFromScannedPointQueue: (state) => {
      state.scannedPointQueue = state.scannedPointQueue.slice(1);
    },
    apiError: (state, action: PayloadAction<ZodError | string | unknown>) => {
      state.apiError = action.payload;
      state.trailLoaded = false;
      state.loading = false;
    },
    clearApiError: (state) => {
      state.apiError = undefined;
    },
  },
});

export const {
  addPointToScannedPointQueue,
  advanceMessagePoint,
  answerQuestion,
  apiError,
  clearApiError,
  setLoading,
  setInviteRequired,
  clearInviteRequired,
  loadSavedTrail,
  loadTrail,
  markMessagesRead,
  removePointFromScannedPointQueue,
  restoreSavedTrail,
  revalidateCurrentTrail,
  closeStorySelector,
  openStorySelector,
  setCurrentPage,
  setPointScanned,
  setViewed,
  stampDeliveries,
} = trail.actions;

export default trail;
